import React, { useState } from "react";
import { X, Wallet, Globe, DollarSign } from "lucide-react";
import { requestWithdrawal } from "../../services/withdrawalModalService";

interface WithdrawalModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const WithdrawalModal: React.FC<WithdrawalModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const [address, setAddress] = useState("");
  const [network, setNetwork] = useState("TRC20");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!address || !network || !amount) {
      alert("Please fill in all fields");
      return;
    }

    try {
      setLoading(true);
      await requestWithdrawal({
        cryptoAddress: address,
        network,
        amount,
      });

      onSuccess();
      setAddress("");
      setNetwork("TRC20");
      setAmount("");
      onClose();
    } catch (error: any) {
      console.error("❌ Withdrawal request failed:", error);
      alert("Failed to send withdrawal request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-center px-4">
      <div className="bg-white dark:bg-[#1a1a1a] rounded-lg p-6 w-full max-w-sm relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-black dark:hover:text-white"
          aria-label="Close"
        >
          <X size={20} />
        </button>
        <h2 className="text-xl font-bold mb-4 text-center">
          Create Withdrawal
        </h2>

        <div className="space-y-4">
          <div className="relative">
            <label className="text-xs text-gray-500 dark:text-gray-300">
              Address
            </label>
            <input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Enter crypto address"
              className="w-full mt-1 p-2 pr-10 rounded border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 text-sm"
            />
            <Wallet
              className="absolute right-3 top-9 text-gray-400"
              size={16}
            />
          </div>

          <div className="relative">
            <label className="text-xs text-gray-500 dark:text-gray-300">
              Network
            </label>
            <input
              value={network}
              onChange={(e) => setNetwork(e.target.value)}
              placeholder="TRC20 / ERC20 / etc."
              className="w-full mt-1 p-2 pr-10 rounded border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 text-sm"
            />
            <Globe className="absolute right-3 top-9 text-gray-400" size={16} />
          </div>

          <div className="relative">
            <label className="text-xs text-gray-500 dark:text-gray-300">
              Withdrawal Amount (USDT)
            </label>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              type="number"
              step="any"
              placeholder="Enter amount"
              className="w-full mt-1 p-2 pr-10 rounded border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 text-sm"
            />
            <DollarSign
              className="absolute right-3 top-9 text-gray-400"
              size={16}
            />
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="mt-5 w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Submitting..." : "Submit a withdrawal request"}
        </button>
      </div>
    </div>
  );
};
